import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getLocale } from 'umi';
import classnames from 'classnames';
import { Tabs } from 'tea-component';
import { LANGUAGE_EN } from '@home/_config/config';
import { getExplainVal } from '@/pages/dashboard/utils/getExplainVal';
import "./index.scss";

const ScrollTabs = (props: any) => {
  const {
    children,
    className,
    scrollClassName = 'dbh-studio-contextbox',
    fixedParentClassName = 'dbh-studio',
    fixedHeight = 300,
    explainInfo = {},
    tabs = [],
    isScroll = true,
    scrollMarginTop = 70,
    refArray: propsRefArray,
    afterDom,
    ...restProps
  } = props;
  const [show, setShow] = useState(false);
  const [activeId, setActiveId] = useState(tabs?.[0]?.id);
  const [isManuallyChanged, setIsManuallyChanged] = useState(false);
  const isEN = getLocale() === LANGUAGE_EN;
  // 滚动高度
  const [scrollTop, setScrollTop] = useState(0);
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  // 根据屏幕尺寸调整 IntersectionObserver 选项
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1920);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // 设置定位的父元素
    const element = document.querySelector(`.${fixedParentClassName}`);
    if (element) {
      (element as HTMLElement).style.transform = 'translateX(0)';
    }
    return () => {
      // 卸载设置的样式
      if (element) {
        (element as HTMLElement).style.transform = "";
      }
    };
  }, []);

  // 监听滚动吸顶
  useEffect(() => {
    const scorllBody = document.getElementsByClassName(scrollClassName)?.[0];
    scorllBody?.addEventListener('scroll', scrollFunc);
    return () => scorllBody?.removeEventListener("scroll", scrollFunc);
  }, []);

  const scrollFunc = (e: any) => {
    const scorllBody = e.target;
    setScrollTop(scorllBody?.scrollTop);
  };

  // 吸顶展示逻辑
  useEffect(() => {
    setShow(scrollTop >= fixedHeight);
  }, [fixedHeight, scrollTop]);

  // 生成选项
  const options = useMemo(() => tabs?.map((item: any) => {
    const { nameEn, nameCn, enTooltip, cnTooltip, rainbowKey, id, disable, explainEn = '', explainCn = '' } = item;
    const explain = getExplainVal(explainInfo, rainbowKey, getLocale(), explainEn, explainCn);

    return {
      id,
      label: isEN ? nameEn : nameCn,
      bubble: explain?.replaceAll('\\n', '<br/>'),
      tooltip: isEN ? enTooltip : cnTooltip,
      disabled: disable !== undefined ? disable : false, // 禁用兜底
    };
  }), [tabs, explainInfo, isEN]);

  // 渲染Tabs组件
  const renderTabs = () => (
    <Tabs
      className={classnames('dbh-scroll-tabs')}
      tabs={options}
      activeId={activeId}
      onActive={handleActive}
      addon={afterDom}
    />
  );

  // 处理Segment选项变化
  const handleActive = (tab: { id: string; }) => {
    const { id } = tab;
    setIsManuallyChanged(true);
    setActiveId(id);

    if (isScroll) {
      // 选中第一个tab时, 滚动到页面顶部
      if (id === tabs[0]?.id) {
        const scorllBody = document.getElementsByClassName(scrollClassName)?.[0];
        if (scorllBody) {
          scorllBody.scrollTo({ top: 0, behavior: "smooth" });
          setIsManuallyChanged(false);
        }
      } else {
        scrollToCard(id);
      }
    }
  };

  // 滚动到对应卡片
  const scrollToCard = (id: string) => {

    const cardElement = window.document.getElementById(id);

    if (cardElement) {
      const observerOptions = {
        root: null,
        rootMargin: '-20% 0px -50% 0px',
        threshold: 0.5
      };

      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // 当卡片进入视口，取消手动更改标记
            setIsManuallyChanged(false);
            // 停止观察
            observer.disconnect();
          }
        });
      }, observerOptions);

      observer.observe(cardElement);

      cardElement.scrollIntoView({ behavior: "smooth", block: "start" });

      setTimeout(() => {
        setIsManuallyChanged(false);
      }, 500);
    }
  };

  const numCards = tabs?.length || 0;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const refArray = propsRefArray ? propsRefArray : Array.from({ length: numCards }, () => useRef(null));

  useEffect(() => {
    if (!isManuallyChanged) {
      let prevScrollTop = 0;

      const callback = (entries: IntersectionObserverEntry[]) => {
        const visibleEntries = entries.filter(entry => entry.isIntersecting);

        if (visibleEntries.length > 0) {
          const sortedEntries = visibleEntries.sort((a, b) => {
            const rectA = a.target.getBoundingClientRect();
            const rectB = b.target.getBoundingClientRect();

            return rectA.top - rectB.top; // 按照从上到下排序
          });

          const currentScrollTop = document.documentElement.scrollTop || document.body.scrollTop;
          const isScrollingDown = currentScrollTop > prevScrollTop;
          prevScrollTop = currentScrollTop;

          let targetEntry;
          if (isScrollingDown) {
            // 向下滚动时，优先选中第一个可见的
            targetEntry = sortedEntries[0];
          } else {
            // 向上滚动时，优先选中最后一个可见的
            targetEntry = sortedEntries[sortedEntries.length - 1];
          }

          const id = targetEntry.target.getAttribute('id');
          setActiveId(id);
        }
      };

      const options = {
        root: null,
        rootMargin: isSmallScreen ? '0px' : '-10% 0px -50% 0px', // 小屏幕不加 margin，大屏幕加 margin
        threshold: isSmallScreen ? 1 : 0.5 // 小屏幕完全可见才触发，大屏幕 50% 可见即可
      };

      // 观察元素可见性
      const observer = new IntersectionObserver(callback, options);

      refArray.forEach((ref: { current: Element; }) => {
        if (ref.current instanceof Element) {
          observer.observe(ref.current);
        }
      });

      // 清理观察者
      return () => {
        refArray.forEach((ref: { current: Element; }) => {
          if (ref.current) {
            observer.unobserve(ref.current);
          }
        });
      };
    }
  }, [isManuallyChanged, isSmallScreen, refArray]);

  return (
    <div className={classnames("dbh-scroll-tabs-wrapper", className)}>
      {/* 吸顶 */}
      {
        show && (
          <div className={`dbh-scroll-header-tabs visible`}>
            {renderTabs()}
          </div>
        )
      }
      {/* Tabs */}
      <div id='ScrollTabs'>
        {renderTabs()}
      </div>
      {/* 内容 */}
      <div id="scroll-box">
        {
          React.Children.map(children, (child: any, index: number) => React.cloneElement(child, {
            ...restProps,
            id: child.id,
            scrollStyle: { scrollMarginTop },
            refArray,
            index
          }))
        }
      </div>
    </div>
  );
};

export default ScrollTabs;